// Top-down cloud shadow for the GEAAR Review Station (CesiumJS 1.142).
// Written by Jake (Z:\Jake\GEAAR_CloudShadow.zip, 13 Oct 2026); ported to TypeScript with named
// cesium imports in place of the window.Cesium global. Logic unchanged. Toggled from
// ui/cloudShadowUI.ts, OFF by default.
//
// One full-screen pass: scene depth -> ground position in local metres -> two
// taps of a tileable noise texture -> darken. Lands on terrain, imagery and
// models alike, skips the sky, needs no shadow map and no per-model shader.
//
//   import { createCloudShadow } from "./cloudShadow";
//   const clouds = createCloudShadow(viewer);          // on
//   clouds.set({ strength: 0.4, windDeg: 250 });       // tweak live
//   clouds.enabled = false;                            // off
//   clouds.destroy();

import {
  Cartesian2, Cartesian3, Cartesian4, Math as CMath, Matrix4, PostProcessStage, Transforms,
  type PerspectiveFrustum, type OrthographicFrustum, type Viewer,
} from "cesium";

const NOISE_N = 512;   // noise period in texels; the canvas is N+1 wide so LINEAR + CLAMP still tiles
const NOISE_CELLS = 16; // cloud blobs across one tile, so the texture repeats every 16 clouds, not every 4

const DEFAULTS = {
  strength: 0.4,       // 0..1 how dark the shadow gets (the panel's Intensity slider stops here and calls it 100%)
  tint: [0.12, 0.12, 0.18], // what the ground is multiplied towards. Keep it near-neutral and dark:
                            // a light blue tint turns red dirt grey-green and it reads as vegetation, not shadow
  size: 3600,          // metres across one shadow blob, layer 1 (the texture then repeats every 58 km)
  size2: 8640,      // same for layer 2, which is rotated so the two repeats never line up
  coverage: 0.4,       // 0 = clear sky, 1 = overcast
  softness: 0.07,     // edge width of the shadow (the two-layer noise only spans about 0.3-0.75, so keep this small)
  speed: 180,          // m/s across the ground, the same at every zoom (the panel's Speed slider runs 180-300
                       // and shows that as 0-100%). Far faster than real wind on purpose:
                       // a real 14 m/s is under 1 pixel a second from 10 km up and looks frozen.
  minBlobPixels: 70,   // zoomed out, blobs grow so they never get smaller than this on screen (stops the blocky mesh look)
  distanceBlur: true, // when zoomed out, shadows beyond the middle of the view soften and fade towards the horizon
  windDeg: 250,        // direction the wind blows FROM, compass degrees
  fps: 20,             // redraws per second to keep the shadow panning while the view is idle. 0 = add no redraws (it then only pans while the view is moving)
  fadeWithSun: true,   // no cloud shadow once the sun is down
};

export type CloudShadowOptions = typeof DEFAULTS & { tint: number[]; origin?: Cartesian3 };

export interface CloudShadow {
  stage: PostProcessStage;
  options: CloudShadowOptions;
  enabled: boolean;
  set(changes: Partial<CloudShadowOptions>): void;
  destroy(): void;
}

function makeNoiseCanvas(): HTMLCanvasElement {
  const n = NOISE_N, w = n + 1;
  let seed = 1337;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  const octaves = [1, 2, 4, 8].map((k, i) => {
    const cells = NOISE_CELLS * k;
    return { cells, amp: 1 / (1 << i), g: Float32Array.from({ length: cells * cells }, rnd) };
  });
  const v = new Float32Array(w * w);
  let lo = Infinity, hi = -Infinity;
  for (let y = 0; y < w; y++) for (let x = 0; x < w; x++) {
    let s = 0;
    for (const { cells, amp, g } of octaves) {
      const fx = (x % n) / n * cells, fy = (y % n) / n * cells;
      const x0 = Math.floor(fx), y0 = Math.floor(fy), x1 = (x0 + 1) % cells, y1 = (y0 + 1) % cells;
      let tx = fx - x0, ty = fy - y0;
      tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
      const a = g[y0 * cells + x0], b = g[y0 * cells + x1], c = g[y1 * cells + x0], d = g[y1 * cells + x1];
      s += amp * (a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty);
    }
    v[y * w + x] = s; lo = Math.min(lo, s); hi = Math.max(hi, s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = w;
  const ctx = canvas.getContext("2d")!, img = ctx.createImageData(w, w);
  for (let i = 0; i < v.length; i++) {
    const c = Math.round((v[i] - lo) / (hi - lo) * 255);
    img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = c; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

const FRAGMENT = `
uniform sampler2D colorTexture;
uniform sampler2D u_noise;
uniform mat4 u_eyeToLocal;  // eye space -> local east/north/up metres
uniform vec4 u_scroll;      // layer offsets in tile units, wrapped every 256 tiles on the CPU
uniform float u_lod;        // smallest a blob may get on screen, in pixels, divided by the blob size in metres
uniform vec4 u_shape;       // 1/tile, 1/tile2, coverage threshold, softness
uniform vec4 u_tint;        // rgb = full-shadow colour, a = strength
uniform vec4 u_pixel;       // metres per pixel = x * distance + y (perspective uses x, top-down ortho uses y);
                            // z = 1 / distance to the ground at the middle of the view, w = how zoomed out (0..1)
in vec2 v_textureCoordinates;

float cloud(vec2 q) {
  return texture(u_noise, fract(q) * ${NOISE_N}.0 / ${NOISE_N + 1}.0 + 0.5 / ${NOISE_N + 1}.0).r;
}

void main() {
  vec4 color = texture(colorTexture, v_textureCoordinates);
  // Terrain depth comes from czm_globeDepthTexture, not depthTexture: with "3D on top"
  // (globe.depthTestAgainstTerrain = false) Cesium wipes the terrain out of the scene depth
  // and the pattern then slides with the camera. Don't fall back to depthTexture where the
  // terrain is missing either: that is Cesium's depth plane and it flashes while tiles load.
  // Raw depth, NOT czm_readDepth: czm_windowToEyeCoordinates undoes the log depth itself.
  float depth = czm_unpackDepth(texture(czm_globeDepthTexture, v_textureCoordinates));
  // Sky (and terrain that has not loaded) is 0.0 in this texture, not 1.0.
  if (depth <= 0.0 || depth >= 1.0 || u_tint.a <= 0.0) { out_FragColor = color; return; }

  vec4 eye = czm_windowToEyeCoordinates(gl_FragCoord.xy, depth);
  eye /= eye.w;
  vec2 p = (u_eyeToLocal * eye).xy;

  // (not czm_metersPerPixel: it goes wrong here once Cesium pushes the near plane out, and the shadow vanishes)
  float mpp = u_pixel.x * -eye.z + u_pixel.y;

  vec2 a = p * u_shape.x + u_scroll.xy;
  vec2 b = mat2(0.799, 0.602, -0.602, 0.799) * p * u_shape.y + u_scroll.zw;
  float n = 0.5 * (cloud(a) + cloud(b));

  // Zoomed out, a blob would shrink to a few pixels and the pattern turns into a blocky mesh (the noise
  // has no mips). So once a blob is under u_lod pixels wide, cross-fade to the same pattern at 2x, 4x, 8x...
  // the size: every pixel always sees blobs of a decent size, close up is untouched.
  float lod = log2(mpp * u_lod);
  if (lod > 0.0) {
    float k = floor(lod), f = lod - k, s = exp2(-k);
    float n0 = k > 0.0 ? 0.5 * (cloud(a * s) + cloud(b * s)) : n;
    float n1 = 0.5 * (cloud(a * s * 0.5) + cloud(b * s * 0.5));
    // blending two patterns flattens the contrast; stretch it back so the amount of shadow stays the same
    n = 0.52 + (mix(n0, n1, f) - 0.52) * inversesqrt(f * f + (1.0 - f) * (1.0 - f));
  }
  // Distance blur: past the middle of the view the edge widens until the shadow is a flat wash, then it fades out.
  // Stops the horizon filling up with squashed streaks when zoomed out.
  float far = u_pixel.w * smoothstep(1.2, 3.2, -eye.z * u_pixel.z);
  float soft = u_shape.w + 0.3 * far;
  float shade = smoothstep(u_shape.z - soft, u_shape.z + soft, n) * (1.0 - far);

  // whole-globe views: past 128x the pan offset stops lining up, so fade out before then
  shade *= u_tint.a * (1.0 - smoothstep(6.0, 7.0, lod));

  out_FragColor = vec4(color.rgb * mix(vec3(1.0), u_tint.rgb, shade), color.a);
}`;

export function createCloudShadow(viewer: Viewer, options: Partial<CloudShadowOptions> = {}): CloudShadow {
  const scene = viewer.scene;
  const o: CloudShadowOptions = { ...DEFAULTS, ...options };

  // Fixed local frame: the pattern is pinned to the ground and never re-anchors (no snapping).
  const origin = o.origin ?? Cartesian3.clone(scene.camera.pickEllipsoid(
    new Cartesian2(scene.canvas.clientWidth / 2, scene.canvas.clientHeight / 2)) ?? scene.camera.positionWC);
  const worldToLocal = Matrix4.inverseTransformation(Transforms.eastNorthUpToFixedFrame(origin), new Matrix4());
  const up = Cartesian3.normalize(origin, new Cartesian3());

  const eyeToLocal = new Matrix4(), scroll = new Cartesian4(), shape = new Cartesian4(), tint = new Cartesian4(), pixel = new Cartesian4();
  // The pan only advances on frames that get drawn, capped per frame, so a pause never turns into a jump.
  let last = performance.now(), warmup = 3;
  const wrap = (x: number) => x - Math.floor(x / 256) * 256; // 256 tiles, so the offset still lines up at every zoomed-out scale

  const stage = new PostProcessStage({
    name: "geaar_cloud_shadow",
    fragmentShader: FRAGMENT,
    uniforms: {
      u_noise: makeNoiseCanvas(),
      u_eyeToLocal: () => Matrix4.multiply(worldToLocal, scene.camera.inverseViewMatrix, eyeToLocal),
      u_scroll: () => {
        const now = performance.now();
        const d = Math.min(now - last, 100) / 1000 * o.speed; last = now; // metres moved this frame
        const a = CMath.toRadians(o.windDeg);
        const wx = Math.sin(a), wy = Math.cos(a); // moving the pattern downwind = adding the upwind vector
        const tile = o.size * NOISE_CELLS, tile2 = o.size2 * NOISE_CELLS;
        scroll.x = wrap(scroll.x + d * wx / tile); scroll.y = wrap(scroll.y + d * wy / tile);
        scroll.z = wrap(scroll.z + d * 0.6 * (0.799 * wx - 0.602 * wy) / tile2);
        scroll.w = wrap(scroll.w + d * 0.6 * (0.602 * wx + 0.799 * wy) / tile2);
        return scroll;
      },
      u_shape: () => Cartesian4.fromElements(
        1 / (o.size * NOISE_CELLS), 1 / (o.size2 * NOISE_CELLS), 0.52 - (o.coverage - 0.5) * 0.5, o.softness, shape),
      u_lod: () => o.minBlobPixels / o.size,
      u_pixel: () => {
        const cam = scene.camera, f = cam.frustum as PerspectiveFrustum & OrthographicFrustum, h = scene.drawingBufferHeight;
        if (f.fovy === undefined) return Cartesian4.fromElements(0, f.width! / scene.drawingBufferWidth, 0, 0, pixel);
        let invFocus = 0, zoomedOut = 0;
        const carto = cam.positionCartographic;
        if (o.distanceBlur && carto) {
          const above = Math.max(carto.height - (scene.globe.getHeight(carto) ?? 0), 1);
          invFocus = Math.max(Math.sin(-cam.pitch), 0.12) / above;
          zoomedOut = CMath.clamp((above - 2000) / 8000, 0, 1); // nothing below 2 km up, full effect from 10 km
        }
        return Cartesian4.fromElements(2 * Math.tan(f.fovy / 2) / h, 0, invFocus, zoomedOut, pixel);
      },
      u_tint: () => {
        // Cesium rebuilds the noise texture when the stage is switched on and samples plain white
        // until it is ready, which would flash the whole ground dark for a frame.
        let k = warmup > 0 ? (warmup--, 0) : o.strength;
        if (o.fadeWithSun) {
          const sun = (scene as unknown as { context: { uniformState: { sunDirectionWC: Cartesian3 } } }).context.uniformState.sunDirectionWC;
          k *= CMath.clamp(Cartesian3.dot(sun, up) / 0.15, 0, 1);
        }
        return Cartesian4.fromElements(o.tint[0], o.tint[1], o.tint[2], k, tint);
      },
    },
  });
  scene.postProcessStages.add(stage);

  // The viewer is render-on-demand, so a panning shadow has to ask for frames (fps: 0 turns that off).
  let timer = 0;
  const tick = () => { if (!document.hidden) scene.requestRender(); };
  const stop = () => { clearInterval(timer); timer = 0; };
  const start = () => { stop(); if (o.fps > 0) timer = window.setInterval(tick, 1000 / o.fps); scene.requestRender(); };
  start();

  return {
    stage,
    options: o,
    get enabled() { return stage.enabled; },
    set enabled(on: boolean) { if (on && !stage.enabled) warmup = 3; stage.enabled = !!on; on ? start() : stop(); scene.requestRender(); },
    set(changes: Partial<CloudShadowOptions>) { Object.assign(o, changes); if (stage.enabled) start(); },
    destroy() { stop(); scene.postProcessStages.remove(stage); scene.requestRender(); },
  };
}
